/* ============================================================
   Savraa COD Form Pro  v2.2

   Captures a deliverable Saudi address:
     Name | Phone (+966 locked) | Address | Building/Apt | District
     | City | Region | Postal code

   ------------------------------------------------------------
   READ THIS BEFORE EDITING

   This script NEVER creates <input> elements. It only enhances fields
   that EasySell already renders: relabel, reorder, validate, hide,
   auto-fill. That is deliberate and must stay that way.

   An input injected by theme JS is not part of the app's form model.
   The shopper would fill it in, it would look perfect, and the value
   would never reach the order. A silently dropped address is worse
   than no field at all.

   So: to COLLECT a new piece of the address, enable that field in the
   EasySell app settings first. This file then picks it up automatically
   on the next page load - every field below is optional and no-ops when
   the app is not rendering it.

   ------------------------------------------------------------
   v2.3 changes (2026-08-29)

   1. POSTAL CODE (AND DISTRICT) WERE LABELLED "(optional)" BUT STILL
      NATIVELY REQUIRED. relabel() only changes the visible label text
      and asterisk - it never touched the actual `required` attribute
      EasySell puts on its <input>. Result: the field looked optional,
      shoppers left it blank exactly as told, and the browser/app's own
      required-field validation silently blocked every submit. This is
      a live checkout-blocking bug, confirmed by placing a real test
      order (#1030) that failed until postal code was filled in.
      Fix: explicitly set/remove the `required` attribute to match
      CFG.requirePostal / CFG.requireDistrict instead of only relabeling.

   ------------------------------------------------------------
   v2.2 changes (2026-08-13)

   1. REGION SELECT WAS UNRECOGNISED AND UNVALIDATED.
      SEL.province matched input[name*="region"] but not
      select[name*="region"]. EasySell renders Region as a <select>, so
      it fell through every branch: no label treatment, and crucially no
      validation - a shopper could submit with Region still on its
      placeholder. Now matched, labelled and required.

   2. ADDED PASS-THROUGH SUPPORT for Building/Apt no., District and
      Postal code. Each is wired, labelled and validated if present and
      silently skipped if the app is not rendering it.

   3. District is now SHOWN rather than mirrored from City. Mirroring
      wrote the city name into the district field, which put a wrong
      value on the shipping label. An empty district is recoverable by
      phone; a wrong one is not.

   v2.1 fixes retained: phone E.164 lock on submit, narrowed submit-button
   resolution, and product/cart form exclusion. Do not undo those.
   ============================================================ */
(function () {
  'use strict';

  var CFG = {
    countryCode: '+966',
    phoneOutput: 'e164',    // 'e164' -> +9665XXXXXXXX | 'local' -> 05XXXXXXXX
    showDistrict: true,     // district is real address data - collect it
    requireDistrict: false, // true = block submit without it
    requirePostal: false,   // KSA postal codes are poorly known - keep optional
    reorder: true,
    collapseNote: true,
    autoProvince: true
  };

  /* ---------- KSA city -> province ---------- */
  var CITIES = [
    ['Riyadh','\u0627\u0644\u0631\u064a\u0627\u0636','Riyadh'],
    ['Diriyah','\u0627\u0644\u062f\u0631\u0639\u064a\u0629','Riyadh'],
    ['Al Kharj','\u0627\u0644\u062e\u0631\u062c','Riyadh'],
    ['Al Majmaah','\u0627\u0644\u0645\u062c\u0645\u0639\u0629','Riyadh'],
    ['Al Zulfi','\u0627\u0644\u0632\u0644\u0641\u064a','Riyadh'],
    ['Dawadmi','\u0627\u0644\u062f\u0648\u0627\u062f\u0645\u064a','Riyadh'],
    ['Shaqra','\u0634\u0642\u0631\u0627\u0621','Riyadh'],
    ['Wadi Ad Dawasir','\u0648\u0627\u062f\u064a \u0627\u0644\u062f\u0648\u0627\u0633\u0631','Riyadh'],
    ['Afif','\u0639\u0641\u064a\u0641','Riyadh'],
    ['Al Quwayiyah','\u0627\u0644\u0642\u0648\u064a\u0639\u064a\u0629','Riyadh'],
    ['Jeddah','\u062c\u062f\u0629','Makkah'],
    ['Makkah','\u0645\u0643\u0629 \u0627\u0644\u0645\u0643\u0631\u0645\u0629','Makkah'],
    ['Taif','\u0627\u0644\u0637\u0627\u0626\u0641','Makkah'],
    ['Rabigh','\u0631\u0627\u0628\u063a','Makkah'],
    ['Al Qunfudhah','\u0627\u0644\u0642\u0646\u0641\u0630\u0629','Makkah'],
    ['Al Lith','\u0627\u0644\u0644\u064a\u062b','Makkah'],
    ['Khulais','\u062e\u0644\u064a\u0635','Makkah'],
    ['Al Jumum','\u0627\u0644\u062c\u0645\u0648\u0645','Makkah'],
    ['Madinah','\u0627\u0644\u0645\u062f\u064a\u0646\u0629 \u0627\u0644\u0645\u0646\u0648\u0631\u0629','Madinah'],
    ['Yanbu','\u064a\u0646\u0628\u0639','Madinah'],
    ['Badr','\u0628\u062f\u0631','Madinah'],
    ['Al Ula','\u0627\u0644\u0639\u0644\u0627','Madinah'],
    ['Khaybar','\u062e\u064a\u0628\u0631','Madinah'],
    ['Dammam','\u0627\u0644\u062f\u0645\u0627\u0645','Eastern Province'],
    ['Khobar','\u0627\u0644\u062e\u0628\u0631','Eastern Province'],
    ['Dhahran','\u0627\u0644\u0638\u0647\u0631\u0627\u0646','Eastern Province'],
    ['Jubail','\u0627\u0644\u062c\u0628\u064a\u0644','Eastern Province'],
    ['Qatif','\u0627\u0644\u0642\u0637\u064a\u0641','Eastern Province'],
    ['Al Ahsa','\u0627\u0644\u0623\u062d\u0633\u0627\u0621','Eastern Province'],
    ['Hofuf','\u0627\u0644\u0647\u0641\u0648\u0641','Eastern Province'],
    ['Hafar Al Batin','\u062d\u0641\u0631 \u0627\u0644\u0628\u0627\u0637\u0646','Eastern Province'],
    ['Ras Tanura','\u0631\u0623\u0633 \u062a\u0646\u0648\u0631\u0629','Eastern Province'],
    ['Abqaiq','\u0628\u0642\u064a\u0642','Eastern Province'],
    ['Khafji','\u0627\u0644\u062e\u0641\u062c\u064a','Eastern Province'],
    ['Nairyah','\u0627\u0644\u0646\u0639\u064a\u0631\u064a\u0629','Eastern Province'],
    ['Buraydah','\u0628\u0631\u064a\u062f\u0629','Qassim'],
    ['Unaizah','\u0639\u0646\u064a\u0632\u0629','Qassim'],
    ['Ar Rass','\u0627\u0644\u0631\u0633','Qassim'],
    ['Al Bukayriyah','\u0627\u0644\u0628\u0643\u064a\u0631\u064a\u0629','Qassim'],
    ['Al Mithnab','\u0627\u0644\u0645\u0630\u0646\u0628','Qassim'],
    ['Abha','\u0623\u0628\u0647\u0627','Asir'],
    ['Khamis Mushait','\u062e\u0645\u064a\u0633 \u0645\u0634\u064a\u0637','Asir'],
    ['Bisha','\u0628\u064a\u0634\u0629','Asir'],
    ['Muhayil','\u0645\u062d\u0627\u064a\u0644 \u0639\u0633\u064a\u0631','Asir'],
    ['Ahad Rafidah','\u0623\u062d\u062f \u0631\u0641\u064a\u062f\u0629','Asir'],
    ['An Namas','\u0627\u0644\u0646\u0645\u0627\u0635','Asir'],
    ['Tabuk','\u062a\u0628\u0648\u0643','Tabuk'],
    ['Duba','\u0636\u0628\u0627\u0621','Tabuk'],
    ['Umluj','\u0623\u0645\u0644\u062c','Tabuk'],
    ['Haql','\u062d\u0642\u0644','Tabuk'],
    ['Al Wajh','\u0627\u0644\u0648\u062c\u0647','Tabuk'],
    ['Tayma','\u062a\u064a\u0645\u0627\u0621','Tabuk'],
    ['Hail','\u062d\u0627\u0626\u0644','Hail'],
    ['Baqaa','\u0628\u0642\u0639\u0627\u0621','Hail'],
    ['Arar','\u0639\u0631\u0639\u0631','Northern Borders'],
    ['Rafha','\u0631\u0641\u062d\u0627\u0621','Northern Borders'],
    ['Turaif','\u0637\u0631\u064a\u0641','Northern Borders'],
    ['Jazan','\u062c\u0627\u0632\u0627\u0646','Jazan'],
    ['Sabya','\u0635\u0628\u064a\u0627','Jazan'],
    ['Abu Arish','\u0623\u0628\u0648 \u0639\u0631\u064a\u0634','Jazan'],
    ['Samtah','\u0635\u0627\u0645\u0637\u0629','Jazan'],
    ['Najran','\u0646\u062c\u0631\u0627\u0646','Najran'],
    ['Sharurah','\u0634\u0631\u0648\u0631\u0629','Najran'],
    ['Al Bahah','\u0627\u0644\u0628\u0627\u062d\u0629','Al Bahah'],
    ['Baljurashi','\u0628\u0644\u062c\u0631\u0634\u064a','Al Bahah'],
    ['Sakaka','\u0633\u0643\u0627\u0643\u0627','Al Jawf'],
    ['Qurayyat','\u0627\u0644\u0642\u0631\u064a\u0627\u062a','Al Jawf'],
    ['Dumat Al Jandal','\u062f\u0648\u0645\u0629 \u0627\u0644\u062c\u0646\u062f\u0644','Al Jawf']
  ];

  var T = {
    name: 'Name',            namePh: 'Full name',
    phone: 'Phone',          phonePh: '5X XXX XXXX',
    address: 'Street address', addressPh: 'Street name and building no.',
    building: 'Building / Apt', buildingPh: 'Flat, floor, landmark (optional)',
    district: 'District',    districtPh: 'Neighbourhood / \u0627\u0644\u062d\u064a',
    city: 'City',            cityPh: 'Start typing your city',
    region: 'Region',        regionPh: 'Select your region',
    postal: 'Postal code',   postalPh: '5 digits (optional)',
    note: 'Order note',      notePh: 'Any delivery instructions?',
    noteAdd: 'Add a note to your order',
    eName: 'Name is required',
    ePhoneReq: 'Phone number is required',
    ePhoneBad: 'Enter 9 digits starting with 5',
    ePhone5: 'Saudi mobile numbers start with 5',
    eAddress: 'Street address is required',
    eCity: 'City is required',
    eRegion: 'Please select your region',
    eDistrict: 'District is required',
    ePostal: 'Postal code must be 5 digits'
  };

  /* ---------- helpers ---------- */
  function arNorm(s){
    return (s || '').toString().trim().toLowerCase()
      .replace(/[\u064B-\u0652\u0640]/g,'')
      .replace(/[\u0623\u0625\u0622\u0671]/g,'\u0627')
      .replace(/\u0649/g,'\u064a')
      .replace(/\u0629/g,'\u0647')
      .replace(/\s+/g,' ');
  }

  function setVal(el, val){
    if(!el) return;
    try{
      var proto = (el instanceof window.HTMLTextAreaElement) ? window.HTMLTextAreaElement.prototype
                : (el instanceof window.HTMLSelectElement)   ? window.HTMLSelectElement.prototype
                : window.HTMLInputElement.prototype;
      var d = Object.getOwnPropertyDescriptor(proto, 'value');
      if(d && d.set) d.set.call(el, val); else el.value = val;
    }catch(e){ el.value = val; }
    el.dispatchEvent(new Event('input',  { bubbles:true }));
    el.dispatchEvent(new Event('change', { bubbles:true }));
  }

  function q(root, sel){ try { return root.querySelector(sel); } catch(e){ return null; } }

  function groupOf(el, root){
    var n = el;
    while(n.parentElement && n.parentElement !== root &&
          n.parentElement.querySelectorAll('input:not([type=hidden]),select,textarea').length === 1){
      n = n.parentElement;
    }
    return n;
  }

  function labelOf(group, input){
    if(input.id && window.CSS && CSS.escape){
      var l = q(group, 'label[for="' + CSS.escape(input.id) + '"]') ||
              q(document, 'label[for="' + CSS.escape(input.id) + '"]');
      if(l) return l;
    }
    return q(group, 'label');
  }

  function relabel(group, input, text, required){
    var html = text + (required ? '<span class="svcod-req" aria-hidden="true">*</span>' : '');
    var l = labelOf(group, input);
    if(l){ l.innerHTML = html; l.classList.add('svcod-label'); return; }
    var s = document.createElement('span');
    s.className = 'svcod-label';
    s.innerHTML = html;
    group.insertBefore(s, group.firstChild);
  }

  // BUGFIX v2.3: keep the actual `required` attribute in sync with the
  // optional/required label we just applied. relabel() only ever touched
  // the visible text - it never removed EasySell's own `required`
  // attribute, so a field marked "(optional)" could still be silently
  // blocked by native/app-level required-field validation. Confirmed
  // live via a real test checkout (order #1030): submit failed on postal
  // code until it was filled in, despite the label reading "optional".
  function setRequired(input, required){
    if(!input) return;
    if(required){ input.setAttribute('required','required'); }
    else{ input.removeAttribute('required'); }
  }

  function errNode(group){
    var e = q(group, '.svcod-err');
    if(!e){
      e = document.createElement('p');
      e.className = 'svcod-err';
      e.setAttribute('role','alert');
      group.appendChild(e);
    }
    return e;
  }
  function fail(g, msg){ if(!g) return; g.classList.add('is-invalid'); g.classList.remove('is-valid'); errNode(g).textContent = msg; }
  function pass(g){ if(!g) return; g.classList.remove('is-invalid'); g.classList.add('is-valid'); }

  /* ---------- phone ---------- */
  function phoneDigits(v){
    var d = (v || '').replace(/\D+/g, '');
    if(d.indexOf('00966') === 0) d = d.slice(5);
    else if(d.indexOf('966') === 0) d = d.slice(3);
    d = d.replace(/^0+/, '');
    return d.slice(0, 9);
  }
  function phoneValid(d){ return /^5[013-9]\d{7}$/.test(d); }
  function phoneOut(d){ return CFG.phoneOutput === 'local' ? '0' + d : '+966' + d; }

  /* ---------- selectors ----------
     province/region now matches <select> too. EasySell renders Region as
     a select and the old input-only pattern let it slip through every
     branch, including validation. */
  var SEL = {
    first:    'input[name*="first" i],input[name*="fname" i],input[name*="name" i]:not([name*="last" i]),input[id*="first" i]',
    last:     'input[name*="last" i],input[name*="lname" i],input[id*="last" i]',
    phone:    'input[type="tel"],input[name*="phone" i],input[name*="mobile" i],input[id*="phone" i]',
    address:  'input[name*="address" i]:not([name*="2" i]),input[name*="adress" i],input[name*="street" i]',
    building: 'input[name*="address2" i],input[name*="apartment" i],input[name*="building" i],input[name*="line2" i],input[name*="unit" i]',
    district: 'input[name*="district" i],input[name*="hai" i],input[name*="hay" i],input[name*="neighborhood" i],input[name*="neighbourhood" i]',
    province: 'select[name*="province" i],input[name*="province" i],select[name*="state" i],input[name*="state" i],select[name*="region" i],input[name*="region" i],select[name*="area" i],select[name*="governorate" i]',
    city:     'select[name*="city" i],input[name*="city" i]',
    postal:   'input[name*="zip" i],input[name*="postal" i],input[name*="postcode" i]',
    note:     'textarea'
  };

  // Containers that are emphatically NOT the COD form. If roots() ever
  // returns one of these, the validation gate ends up bolted onto Dawn's
  // Add to Cart button and blocks it until COD fields are filled.
  var NOT_COD = [
    'product-form',
    'form[action*="/cart/add"]',
    'form[action$="/cart"]',
    'form[data-type="add-to-cart-form"]',
    'cart-drawer',
    'cart-items',
    '.product-form',
    '#MainContent .shopify-section--main-cart-items'
  ].join(',');

  function isProductOrCartForm(el){
    if(!el) return true;
    try{
      if(el.matches && el.matches(NOT_COD)) return true;
      if(el.closest && el.closest('product-form,cart-drawer,cart-items')) return true;
      if(el.querySelector('[name="add"],input.product-variant-id,input[name="id"][data-\\-]')) return true;
      if(el.querySelector('button[name="add"]')) return true;
    }catch(e){}
    return false;
  }

  function hide(group, input, fillWith){
    if(!group) return;
    group.classList.add('svcod-hidden');
    group.setAttribute('aria-hidden','true');
    if(input){
      input.removeAttribute('required');
      if(typeof fillWith === 'string' && !String(input.value || '').trim()) setVal(input, fillWith);
    }
  }

  // A <select> still sitting on its placeholder option.
  function selectEmpty(el){
    if(!el) return true;
    if(el.tagName !== 'SELECT') return !String(el.value || '').trim();
    var v = String(el.value || '').trim();
    if(!v) return true;
    var txt = arNorm(el.options[el.selectedIndex] ? el.options[el.selectedIndex].text : '');
    return txt === arNorm(T.region) || txt === 'region' || txt === 'select';
  }

  /* ---------- enhance ---------- */
  function enhance(form){
    if(form.dataset.svcod === '23') return;

    var f = {
      first:    q(form, SEL.first),
      last:     q(form, SEL.last),
      phone:    q(form, SEL.phone),
      address:  q(form, SEL.address),
      building: q(form, SEL.building),
      district: q(form, SEL.district),
      province: q(form, SEL.province),
      city:     q(form, SEL.city),
      postal:   q(form, SEL.postal),
      note:     q(form, SEL.note)
    };
    if(!f.phone) return;

    form.dataset.svcod = '23';
    form.classList.add('svcod-form','svcod-form--en','notranslate');
    form.setAttribute('translate','no');
    form.setAttribute('dir','ltr');

    var G = {};
    Object.keys(f).forEach(function(k){
      if(f[k]){ G[k] = groupOf(f[k], form); G[k].classList.add('svcod-group'); }
    });

    /* 1. Name */
    if(f.first){
      relabel(G.first, f.first, T.name, true);
      f.first.placeholder = T.namePh;
      f.first.setAttribute('autocomplete','name');
      f.first.setAttribute('autocapitalize','words');
      if(f.last && f.last !== f.first){
        hide(G.last, f.last);
        f.first.addEventListener('input', function(){
          var p = f.first.value.trim().split(/\s+/);
          setVal(f.last, p.length > 1 ? p.slice(1).join(' ') : '-');
        });
      }
    }

    /* 2. Phone, +966 locked */
    (function(){
      var wrap = f.phone.parentElement;
      wrap.classList.add('svcod-inputwrap');
      G.phone.classList.add('svcod-tel');
      relabel(G.phone, f.phone, T.phone, true);

      if(!q(wrap, '.svcod-cc')){
        var cc = document.createElement('span');
        cc.className = 'svcod-cc';
        cc.innerHTML = '<span aria-hidden="true">\uD83C\uDDF8\uD83C\uDDE6</span>' + CFG.countryCode;
        wrap.insertBefore(cc, f.phone);
      }
      f.phone.type = 'tel';
      f.phone.setAttribute('inputmode','numeric');
      f.phone.setAttribute('autocomplete','tel-national');
      f.phone.setAttribute('maxlength','16');
      f.phone.placeholder = T.phonePh;

      function fmt(){
        var d = phoneDigits(f.phone.value);
        var pretty = d.replace(/^(\d{2})(\d{0,3})(\d{0,4}).*$/, function(_, a, b, c){
          return [a, b, c].filter(Boolean).join(' ');
        });
        if(pretty !== f.phone.value) setVal(f.phone, pretty);
        return d;
      }

      f.phone.addEventListener('input', function(){
        // One-shot lock: validate() has just written the final submit value
        // (+9665XXXXXXXX). Without this guard fmt() immediately strips the
        // country code back off and the app receives "5X XXX XXXX".
        if(f.phone.dataset.svcodLock === '1'){
          f.phone.dataset.svcodLock = '';
          return;
        }
        var d = fmt();
        if(!d){ G.phone.classList.remove('is-invalid','is-valid'); return; }
        if(d.length === 9 && phoneValid(d)) pass(G.phone);
        else if(d.charAt(0) !== '5') fail(G.phone, T.ePhone5);
        else G.phone.classList.remove('is-invalid');
      });
      f.phone.addEventListener('blur', function(){
        var d = phoneDigits(f.phone.value);
        if(d && !phoneValid(d)) fail(G.phone, T.ePhoneBad);
      });
    })();

    /* 3. Street address */
    if(f.address){
      relabel(G.address, f.address, T.address, true);
      f.address.placeholder = T.addressPh;
      f.address.setAttribute('autocomplete','address-line1');
    }

    /* 4. Building / Apt - optional, no-ops if the app is not rendering it */
    if(f.building){
      relabel(G.building, f.building, T.building, false);
      f.building.placeholder = T.buildingPh;
      f.building.setAttribute('autocomplete','address-line2');
    }

    /* 5. District - shown, not mirrored.
       Mirroring the city name into district put a wrong value on the
       shipping label. Blank is recoverable by phone; wrong is not.
       BUGFIX v2.3: also sync the native `required` attribute so an
       optional district can't silently block submit. */
    if(f.district){
      if(CFG.showDistrict){
        relabel(G.district, f.district, T.district, CFG.requireDistrict);
        f.district.placeholder = T.districtPh;
        f.district.setAttribute('autocomplete','address-level3');
        setRequired(f.district, CFG.requireDistrict);
      } else {
        hide(G.district, f.district, '-');
      }
    }

    /* 6. City + silent province derivation */
    if(f.city && f.city.tagName === 'INPUT'){
      relabel(G.city, f.city, T.city, true);
      f.city.placeholder = T.cityPh;
      f.city.setAttribute('autocomplete','address-level2');

      if(!document.getElementById('svcod-cities')){
        var dl = document.createElement('datalist');
        dl.id = 'svcod-cities';
        dl.innerHTML = CITIES.map(function(c){
          return '<option value="' + c[0] + '">' + c[1] + '</option>';
        }).join('');
        document.body.appendChild(dl);
      }
      f.city.setAttribute('list','svcod-cities');

      if(CFG.autoProvince && f.province){
        var map = {};
        CITIES.forEach(function(c){ map[arNorm(c[0])] = c[2]; map[arNorm(c[1])] = c[2]; });
        var apply = function(){
          var p = map[arNorm(f.city.value)];
          if(!p) return;
          if(f.province.tagName === 'SELECT'){
            var opt = null, i;
            for(i = 0; i < f.province.options.length; i++){
              if(f.province.options[i].value === p ||
                 arNorm(f.province.options[i].text).indexOf(arNorm(p)) > -1){
                opt = f.province.options[i]; break;
              }
            }
            if(opt) setVal(f.province, opt.value);
          } else {
            setVal(f.province, p);
          }
        };
        f.city.addEventListener('input', apply);
        f.city.addEventListener('change', apply);
        hide(G.province, null);
      }
    } else if(f.province){
      // No City field is being rendered by the app, so Region is the only
      // geographic data we get. Label it and REQUIRE it - previously a
      // shopper could submit with the select still on its placeholder.
      relabel(G.province, f.province, T.region, true);
      if(f.province.tagName === 'INPUT') f.province.placeholder = T.regionPh;
      f.province.addEventListener('change', function(){
        if(!selectEmpty(f.province)) pass(G.province);
      });
    }

    /* 7. Postal code - optional by default.
       BUGFIX v2.3: EasySell renders this input with a native `required`
       attribute regardless of our label. Relabeling it "(optional)"
       without also removing that attribute left it cosmetically optional
       but still enforced - shoppers who left it blank (as told) had
       their submit silently blocked. Confirmed live via test order #1030. */
    if(f.postal){
      relabel(G.postal, f.postal, T.postal, CFG.requirePostal);
      f.postal.placeholder = T.postalPh;
      f.postal.setAttribute('inputmode','numeric');
      f.postal.setAttribute('autocomplete','postal-code');
      f.postal.setAttribute('maxlength','5');
      setRequired(f.postal, CFG.requirePostal);
    }

    /* 8. Note, collapsed */
    if(CFG.collapseNote && f.note && G.note){
      relabel(G.note, f.note, T.note, false);
      f.note.placeholder = T.notePh;
      f.note.setAttribute('rows','2');
      f.note.setAttribute('translate','no');
      G.note.hidden = true;

      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'svcod-notetoggle';
      btn.setAttribute('aria-expanded','false');
      btn.innerHTML = '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"><path d="M12 5v14M5 12h14"/></svg> ' + T.noteAdd;
      G.note.parentNode.insertBefore(btn, G.note);
      btn.addEventListener('click', function(){
        var open = G.note.hidden;
        G.note.hidden = !open;
        btn.setAttribute('aria-expanded', String(open));
        if(open) f.note.focus();
      });
    }

    /* 9. Order: broad -> narrow, the way an address is actually written */
    if(CFG.reorder){
      try{
        var order = [G.first, G.phone, G.address, G.building, G.district, G.city, G.province, G.postal].filter(Boolean);
        var anchor = order[0];
        for(var i = 1; i < order.length; i++){
          if(anchor.parentNode === order[i].parentNode){
            anchor.parentNode.insertBefore(order[i], anchor.nextSibling);
            anchor = order[i];
          }
        }
      }catch(e){}
    }

    /* 10. Submit gate
       Narrow, ordered resolution. querySelector on a selector LIST resolves
       in document order, so a quantity stepper or variant swatch could win
       over the real submit. We probe one selector at a time, most specific
       first, and reject anything that is clearly a secondary control. */
    function findSubmit(scope){
      var probes = [
        'button[type="submit"]',
        'input[type="submit"]',
        'button[name="cod-submit"]',
        'button[class*="submit" i]',
        'a[class*="submit" i]'
      ];
      var reject = 'button[type="button"],[data-quantity],[class*="quantity" i],' +
                   '[class*="qty" i],[class*="swatch" i],[class*="variant" i],' +
                   '[class*="close" i],[class*="note" i],.svcod-notetoggle';
      for(var i = 0; i < probes.length; i++){
        var list = scope.querySelectorAll(probes[i]);
        for(var j = 0; j < list.length; j++){
          var el = list[j];
          try{ if(el.matches(reject)) continue; }catch(e){}
          return el;
        }
      }
      return null;
    }

    var submitBtn = findSubmit(form);
    if(submitBtn) submitBtn.classList.add('svcod-submit');

    function validate(){
      var ok = true, bad = null;

      if(f.first){
        if(!f.first.value.trim()){ fail(G.first, T.eName); ok = false; bad = bad || f.first; }
        else pass(G.first);
      }

      var d = phoneDigits(f.phone.value);
      if(!phoneValid(d)){
        fail(G.phone, d ? T.ePhoneBad : T.ePhoneReq);
        ok = false; bad = bad || f.phone;
      } else {
        pass(G.phone);
        // Arm the one-shot lock BEFORE writing, so the field's own input
        // handler does not reformat the E.164 value back to local.
        f.phone.dataset.svcodLock = '1';
        setVal(f.phone, phoneOut(d));
      }

      var required = [['address', f.address, T.eAddress]];
      if(f.city) required.push(['city', f.city, T.eCity]);
      if(CFG.showDistrict && CFG.requireDistrict && f.district) required.push(['district', f.district, T.eDistrict]);

      required.forEach(function(row){
        var el = row[1];
        if(!el) return;
        if(!String(el.value || '').trim()){ fail(G[row[0]], row[2]); ok = false; bad = bad || el; }
        else pass(G[row[0]]);
      });

      // Region: only gate it when it is actually visible to the shopper.
      // When a City field exists the province is derived and hidden, and
      // blocking on a field nobody can see is an unfixable dead end.
      if(f.province && G.province && !G.province.classList.contains('svcod-hidden')){
        if(selectEmpty(f.province)){ fail(G.province, T.eRegion); ok = false; bad = bad || f.province; }
        else pass(G.province);
      }

      if(f.postal && String(f.postal.value || '').trim()){
        if(!/^\d{5}$/.test(f.postal.value.trim())){ fail(G.postal, T.ePostal); ok = false; bad = bad || f.postal; }
        else pass(G.postal);
      }

      if(!ok && bad){
        try{ bad.focus(); bad.scrollIntoView({ behavior:'smooth', block:'center' }); }catch(e){}
      }
      return ok;
    }

    function block(e){
      if(!validate()){
        e.preventDefault();
        e.stopPropagation();
        if(e.stopImmediatePropagation) e.stopImmediatePropagation();
        return false;
      }
      form.classList.add('is-busy');
      setTimeout(function(){ form.classList.remove('is-busy'); }, 8000);
    }

    if(form.tagName === 'FORM'){
      form.setAttribute('novalidate','novalidate');
      form.addEventListener('submit', block, true);
    }
    if(submitBtn) submitBtn.addEventListener('click', block, true);
  }

  /* ---------- discovery ---------- */
  function roots(){
    var out = [];
    var tels = document.querySelectorAll(SEL.phone);
    Array.prototype.forEach.call(tels, function(t){
      // Never enhance a phone field that lives inside Dawn's product or
      // cart machinery - that is how the gate used to block Add to Cart.
      try{ if(t.closest('product-form,cart-drawer,cart-items,form[action*="/cart"]')) return; }catch(e){}

      var r = t.closest ? t.closest('form') : null;
      if(!r){
        var n = t.parentElement;
        while(n && n !== document.body){
          if(n.querySelectorAll('input:not([type=hidden]),select,textarea').length >= 3){ r = n; break; }
          n = n.parentElement;
        }
      }
      if(!r) return;
      if(isProductOrCartForm(r)) return;
      if(out.indexOf(r) === -1) out.push(r);
    });
    return out;
  }

  var tick;
  function scan(){
    clearTimeout(tick);
    tick = setTimeout(function(){ roots().forEach(enhance); }, 60);
  }

  if(document.readyState === 'loading'){
    document.addEventListener('DOMContentLoaded', scan, { once:true });
  } else {
    scan();
  }

  new MutationObserver(scan).observe(document.documentElement, { childList:true, subtree:true });
})();
